import {
    ArrowRight,
    BellRing,
    CalendarDays,
    CheckCircle2,
    Clock3,
    Home,
    ListChecks,
    Mail,
    MessageCircle,
    ShieldCheck,
    Sparkles,
    UsersRound,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { backendUrl, preregisterUrl } from '../lib/apiUrls';
import { cn } from '../lib/cn';
import { BearyGuide, BearySprite } from '../components/ui/BearySprite';
import { PublicFooter } from '../components/PublicSiteLayout';

const waitlistStorageKey = 'littleAddaPreregisterEmail';

const parentModes = [
  {
    id: 'school-email',
    label: 'School email',
    time: '8:42 AM',
    title: 'Important school update found',
    parentNote: 'Little Adda noticed picture day, a lunch order deadline, and a volunteer form in one long school email.',
    accent: 'bg-teal-600',
    tint: 'bg-teal-50 text-teal-900 border-teal-200',
    progress: 74,
    items: [
      ['Found', 'Picture day is Friday', Mail],
      ['Due', 'Lunch order closes tonight', BellRing],
      ['Form', 'Volunteer signup attached', CheckCircle2],
    ],
  },
  {
    id: 'approve',
    label: 'Approve',
    time: '8:44 AM',
    title: 'Nothing gets added without you',
    parentNote: 'Review the suggested reminder, task, and calendar hold. Approve, edit, or dismiss each one.',
    accent: 'bg-sky-600',
    tint: 'bg-sky-50 text-sky-900 border-sky-200',
    progress: 50,
    items: [
      ['Reminder', 'Order lunch by 7 PM', BellRing],
      ['Task', 'Complete volunteer form', ListChecks],
      ['Calendar', 'Hold Friday picture day', CalendarDays],
    ],
  },
  {
    id: 'family-flow',
    label: 'Family flow',
    time: '6:10 PM',
    title: 'Approved updates become family flow',
    parentNote: 'The right parent sees the right next step. Caregivers stay informed without another group-chat scramble.',
    accent: 'bg-amber-500',
    tint: 'bg-amber-50 text-amber-950 border-amber-200',
    progress: 92,
    items: [
      ['Shared', 'Dad gets lunch reminder', UsersRound],
      ['Ready', 'Calendar suggestion saved', CheckCircle2],
      ['Brief', 'Tomorrow school prep ready', Sparkles],
    ],
  },
];

function normalizeEmail(email) {
  return email.trim().toLowerCase();
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function LittleAddaPage({ currentUser }) {
  const [email, setEmail] = useState('');
  const [activeModeId, setActiveModeId] = useState(parentModes[0].id);
  const [status, setStatus] = useState({ type: 'idle', message: 'Join the early access list.' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeMode = useMemo(
    () => parentModes.find((mode) => mode.id === activeModeId) || parentModes[0],
    [activeModeId]
  );

  useEffect(() => {
    const savedEmail = window.localStorage.getItem(waitlistStorageKey);
    if (savedEmail) {
      setEmail(savedEmail);
      setStatus({ type: 'success', message: 'You are already preregistered on this browser.' });
    }
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    const preregisterEmail = normalizeEmail(email);

    if (!preregisterEmail) {
      setStatus({ type: 'error', message: 'Enter an email address to preregister.' });
      return;
    }

    if (!isValidEmail(preregisterEmail)) {
      setStatus({ type: 'error', message: 'Enter a valid email address.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'loading', message: 'Preregistering...' });

    try {
      let response;

      try {
        response = await fetch(preregisterUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: preregisterEmail,
            source: 'little-adda-homepage',
            product: 'little-adda',
          }),
        });
      } catch {
        throw new Error(`Could not reach backend at ${backendUrl}.`);
      }

      let payload = {};
      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(
          payload?.error ||
            payload?.message ||
            `Preregister failed with status ${response.status}.`
        );
      }

      window.localStorage.setItem(waitlistStorageKey, preregisterEmail);
      setEmail(preregisterEmail);
      setStatus({
        type: 'success',
        message: payload?.message || 'Thanks. You are preregistered for Little Adda.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Could not preregister. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const statusClassName =
    status.type === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : status.type === 'error'
        ? 'border-red-200 bg-red-50 text-red-800'
        : status.type === 'loading'
          ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-blue-200 bg-blue-50 text-blue-800';

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#f7faf7] text-slate-950">
      <section className="relative isolate min-h-screen overflow-hidden">
        <img
          src="/images/little-adda-parent-hero.png"
          alt=""
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(247,250,247,0.97)_0%,rgba(247,250,247,0.9)_34%,rgba(247,250,247,0.38)_66%,rgba(15,23,42,0.2)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-48 bg-[linear-gradient(180deg,rgba(247,250,247,0),#f7faf7)]" />

        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-5 sm:px-8">
          <Link to="/" className="flex items-center gap-3 text-slate-950 no-underline">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-700 text-base font-black text-white shadow-subtle">
              LA
            </span>
            <span>
              <span className="block text-lg font-black leading-tight">Little Adda</span>
              <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Family copilot
              </span>
            </span>
          </Link>

          {currentUser ? (
            <Link
              to="/history"
              className="hidden shrink-0 items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white no-underline shadow-subtle transition hover:bg-teal-800 sm:inline-flex"
            >
              <span>Open app</span>
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/login"
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="rounded-xl bg-teal-700 px-4 py-2 text-sm font-bold text-white shadow-subtle transition hover:bg-teal-800"
              >
                Get started
              </Link>
            </div>
          )}
        </nav>

        <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-20 pt-8 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(380px,540px)] lg:items-center lg:pt-10">
          <div className="w-full max-w-[22rem] min-w-0 sm:max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/85 px-3 py-2 text-sm font-bold text-teal-800 shadow-subtle">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              School-email intelligence for parents
            </div>
            <h1 className="mt-6 max-w-[11ch] text-[2.18rem] font-black leading-[1.04] tracking-normal text-slate-950 sm:max-w-3xl sm:text-6xl lg:text-7xl">
              An app that does not just schedule. It understands parenting.
            </h1>
            <p className="mt-6 max-w-[22rem] text-base leading-8 text-slate-600 sm:max-w-[34rem] sm:text-xl">
              Connect your school emails. Little Adda finds important updates and turns them into helpful reminders, tasks, and calendar suggestions. You approve everything.
            </p>

            <form
              className="mt-8 grid max-w-[22rem] gap-3 rounded-[22px] border border-white/80 bg-white/90 p-3 shadow-panel sm:max-w-[34rem] sm:grid-cols-[minmax(0,1fr)_auto]"
              style={{ width: '100%' }}
              onSubmit={handleSubmit}
            >
              <label className="sr-only" htmlFor="little-adda-email">
                Email address
              </label>
              <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <Mail className="h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
                <input
                  id="little-adda-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="your@email.com"
                  className="min-w-0 flex-1 bg-transparent text-base font-semibold text-slate-950 outline-none placeholder:text-slate-400"
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-teal-700 px-5 py-3 text-base font-black text-white transition hover:bg-teal-800 focus:outline-none focus:ring-4 focus:ring-teal-700/20"
              >
                <span>{isSubmitting ? 'Preregistering...' : 'Preregister'}</span>
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </button>
              <p className={cn('flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold sm:col-span-2', statusClassName)}>
                {status.type === 'loading' ? (
                  <BearySprite preset="loading" size={34} label="Beary is helping load this" />
                ) : null}
                {status.message}
              </p>
            </form>

            <div className="mt-7 grid max-w-[22rem] gap-3 sm:max-w-[34rem] sm:grid-cols-3" style={{ width: '100%' }}>
              {[
                ['Email', 'school updates detected'],
                ['Approve', 'every suggestion first'],
                ['Flow', 'tasks and calendar next'],
              ].map(([value, label]) => (
                <div key={label} className="rounded-2xl border border-white/80 bg-white/70 p-4 shadow-subtle">
                  <p className="m-0 text-2xl font-black text-slate-950">{value}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative min-h-[590px] lg:min-h-[660px]">
            <div className="absolute left-0 top-0 hidden rounded-[22px] border border-amber-200 bg-amber-50 p-4 text-amber-950 shadow-panel xl:block">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-400 text-slate-950">
                  <MessageCircle className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="m-0 text-xs font-black uppercase tracking-[0.14em]">Parent ping</p>
                  <p className="mt-1 text-sm font-bold">School email needs review</p>
                </div>
              </div>
            </div>

            <div
              className="absolute bottom-8 right-0 max-w-[430px] rounded-[28px] border border-white/80 bg-white/82 p-2 shadow-panel backdrop-blur-xl sm:rounded-[34px] sm:p-4 lg:right-3"
              style={{ width: 'min(100%, calc(100vw - 40px))' }}
            >
              <BearyGuide
                className="mb-3 bg-white/86"
                preset="guide"
                size={72}
                message="Beary keeps parent approvals visible before anything joins the family plan."
              />
              <div className="overflow-hidden rounded-[26px] bg-slate-950 text-white">
                <div className="bg-[linear-gradient(135deg,#134e4a,#0f172a_60%,#78350f)] p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="m-0 text-xs font-bold uppercase tracking-[0.16em] text-teal-100">School inbox</p>
                      <p className="mt-1 text-xl font-black sm:text-2xl">Parent approval queue</p>
                    </div>
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/14 text-teal-100">
                      <Home className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>

                  <div className="mt-5 grid gap-2 rounded-2xl bg-white/10 p-2 sm:grid-cols-3">
                    {parentModes.map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => setActiveModeId(mode.id)}
                        className={cn(
                          'min-h-10 rounded-xl px-2 py-2 text-center text-[0.7rem] font-black leading-tight text-white/70 transition hover:bg-white/10 hover:text-white',
                          activeMode.id === mode.id && 'bg-white text-slate-950 hover:bg-white hover:text-slate-950'
                        )}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 p-4 sm:p-5">
                  <div className={cn('rounded-[22px] border p-4', activeMode.tint)}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="m-0 flex items-center gap-2 text-sm font-black">
                          <Clock3 className="h-4 w-4" aria-hidden="true" />
                          {activeMode.time}
                        </p>
                        <h2 className="mt-3 text-xl font-black leading-tight text-slate-950 sm:text-2xl">{activeMode.title}</h2>
                      </div>
                      <div className="relative h-16 w-16 shrink-0">
                        <svg className="h-16 w-16 -rotate-90" viewBox="0 0 44 44" aria-hidden="true">
                          <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(15,23,42,0.14)" strokeWidth="5" />
                          <circle
                            cx="22"
                            cy="22"
                            r="18"
                            fill="none"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeWidth="5"
                            strokeDasharray={`${activeMode.progress * 1.13} 113`}
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-black">
                          {activeMode.progress}
                        </span>
                      </div>
                    </div>
                    <p className="mt-4 text-sm font-semibold leading-6 text-slate-700">{activeMode.parentNote}</p>
                  </div>

                  <div className="grid gap-3">
                    {activeMode.items.map(([tag, title, Icon], index) => (
                      <div
                        key={title}
                        className="grid grid-cols-[46px_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl bg-white/10 p-3 transition hover:bg-white/16"
                      >
                        <span className={cn('flex h-11 w-11 items-center justify-center rounded-2xl text-white', activeMode.accent)}>
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <p className="m-0 text-xs font-black uppercase tracking-[0.12em] text-slate-400">{tag}</p>
                          <p className="mt-1 truncate text-sm font-bold text-white">{title}</p>
                        </div>
                        <span className="text-lg font-black text-white/40">0{index + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute right-1 top-20 hidden max-w-[210px] rounded-[22px] border border-teal-200 bg-white/92 p-4 text-slate-950 shadow-panel backdrop-blur sm:block">
              <p className="m-0 text-xs font-black uppercase tracking-[0.14em] text-teal-700">Shared with</p>
              <div className="mt-3 flex -space-x-3">
                {['P', 'M', 'D', '+'].map((label) => (
                  <span
                    key={label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-slate-950 text-sm font-black text-white"
                  >
                    {label}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-sm font-semibold leading-5 text-slate-600">Parents stay in control before anything reaches the family plan.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-4 px-5 pb-16 sm:px-8 md:grid-cols-3">
        {[
          [Mail, 'Connect school emails', 'Forward or connect the inbox where newsletters, teacher notes, forms, and deadline updates already arrive.'],
          [Sparkles, 'Understand the parenting context', 'Little Adda separates important school action from noise and suggests what a parent likely needs next.'],
          [ShieldCheck, 'Approve every change', 'Suggested reminders, tasks, and calendar holds wait for your approval before joining the family plan.'],
        ].map(([Icon, title, copy]) => (
          <article key={title} className="rounded-[22px] border border-slate-200 bg-white p-6 shadow-subtle transition hover:-translate-y-1 hover:shadow-panel">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-800">
              <Icon className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-5 text-xl font-black text-slate-950">{title}</h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">{copy}</p>
          </article>
        ))}
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
        <div className="grid gap-8 rounded-[28px] border border-slate-200 bg-white p-6 shadow-subtle lg:grid-cols-[0.9fr_1.1fr] lg:p-8">
          <div>
            <p className="m-0 text-xs font-black uppercase tracking-[0.18em] text-teal-700">
              How it works
            </p>
            <h2 className="mt-4 max-w-xl text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
              School email becomes parent action without losing your control.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-8 text-slate-600">
              Little Adda is built for the messy reality of parenting: half-read newsletters, last-minute forms, activity changes, and multiple adults who need different next steps.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['01', 'Connect school emails', 'Give Little Adda a school inbox or forwarded updates.'],
              ['02', 'Find what matters', 'It looks for dates, deadlines, forms, fees, supplies, and schedule changes.'],
              ['03', 'Suggest next steps', 'Reminders, tasks, and calendar holds are drafted for review.'],
              ['04', 'Approve the plan', 'Nothing is added or shared until a parent approves it.'],
            ].map(([number, title, copy]) => (
              <div key={title} className="rounded-[22px] border border-slate-200 bg-slate-50 p-5">
                <p className="m-0 text-sm font-black text-teal-700">{number}</p>
                <h3 className="mt-3 text-lg font-black text-slate-950">{title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            ['/school-email-assistant', 'School email assistant', 'See how Little Adda finds actionable dates, forms, fees, supplies, and changes.'],
            ['/family-calendar-app', 'Family calendar', 'Learn how school dates become parent-reviewed calendar suggestions.'],
            ['/shared-family-task-manager', 'Shared family tasks', 'Turn school requests into clear next steps caregivers can coordinate.'],
          ].map(([to, title, copy]) => (
            <Link key={to} to={to} className="group rounded-[22px] border border-slate-200 bg-white p-6 text-slate-950 no-underline shadow-subtle transition hover:-translate-y-1 hover:border-teal-300 hover:shadow-panel">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-teal-700">Explore</p>
              <h2 className="mt-4 text-xl font-black group-hover:text-teal-800">{title}</h2>
              <p className="mt-3 text-sm leading-7 text-slate-600">{copy}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-black text-teal-800">Learn more <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>
      <PublicFooter />
    </main>
  );
}

export default LittleAddaPage;
